
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Client } from '@/models/Client';
import { useWorkoutTypes } from '@/hooks/useWorkoutTypes';

interface ClientSubscriptionsProps {
  client: Client;
  maxDisplay?: number;
}

const ClientSubscriptions: React.FC<ClientSubscriptionsProps> = ({ client, maxDisplay }) => {
  const { t } = useTranslation();
  const { workoutTypes = [] } = useWorkoutTypes();

  const getWorkoutTypeName = (workoutTypeId: string) => {
    const workoutType = workoutTypes.find(wt => wt.id === workoutTypeId);
    return workoutType?.name || t('clients.unknownWorkoutType');
  };

  const activeSubscriptions = client.subscriptions.filter(sub => sub.isActive);
  const displaySubscriptions = maxDisplay 
    ? activeSubscriptions.slice(0, maxDisplay) 
    : activeSubscriptions;
  const hasMoreSubscriptions = maxDisplay && activeSubscriptions.length > maxDisplay;

  if (activeSubscriptions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1">
      <h4 className="text-sm font-medium text-muted-foreground">
        {t('clients.subscriptions')}
      </h4>
      <div className="grid grid-cols-1 gap-1">
        {displaySubscriptions.map(subscription => {
          const remainingSessions = subscription.sessionsPerMonth - subscription.currentCycleSessionsUsed;
          const isLowSessions = remainingSessions <= 1;
          
          return (
            <div key={subscription.id} className="flex items-center justify-between text-xs">
              <span className="truncate greek-text">
                {getWorkoutTypeName(subscription.workoutTypeId)}
              </span>
              <span className={`font-medium ${
                isLowSessions 
                  ? 'text-orange-600 dark:text-orange-400' 
                  : 'text-muted-foreground'
              }`}>
                {subscription.currentCycleSessionsUsed}/{subscription.sessionsPerMonth}
                {isLowSessions && <span className="ml-1 text-orange-500 dark:text-orange-400">⚠</span>}
              </span>
            </div>
          );
        })}
        {hasMoreSubscriptions && (
          <div className="text-xs text-muted-foreground italic">
            +{activeSubscriptions.length - maxDisplay!} {t('clients.moreSubscriptions')}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientSubscriptions;
